import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, Observable, throwError } from 'rxjs';
import { SnackbarService } from '../widgets/snackbar/snackbar.service';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
    // Session keep-alive is best-effort; network/CORS blips must not surface as user-facing errors.
    private readonly IGNORE_URLS = ['assets/i18n', '/rest/auth/session/current'];

    constructor(
        private router: Router,
        private notificationService: SnackbarService,
        private translateService: TranslateService
    ) {
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.IGNORE_URLS.some((ignored) => request.url.includes(ignored))) {
            return next.handle(request);
        }
        return next.handle(request).pipe(catchError((response) => this.handleError(response)));
    }

    private handleError(response) {
        if (!(response instanceof HttpErrorResponse)) {
            return throwError(() => response);
        }

        if (response.status === 401) {
            return throwError(() => response);
        }

        if (response.status === 403) {
            this.router.navigate(['']);
            return EMPTY;
        }

        this.callNotification(response);

        return throwError(() => response);
    }

    private callNotification(response: HttpErrorResponse) {
        if (!response.error?.systemCodeDescription) {
            this.notificationService.addError('notifications.unexpectedError');
            return;
        }

        let translated: string;
        const translationKey = 'adminResponseCodes.' + response.error.systemCodeDescription;

        const additionalInfo = response.error.additionalInfo;
        if (additionalInfo?.cause) {
            const causeKey = 'gatewayCodes.' + additionalInfo.cause;
            const translatedCause = this.translateService.instant(causeKey);
            translated = this.translateService.instant(translationKey, { ...additionalInfo, cause: translatedCause });
        } else {
            translated = this.translateService.instant(translationKey, response.error.additionalInfo);
        }

        if (translationKey === translated) {
            this.notificationService.addError('notifications.unexpectedError');
            return;
        }

        this.notificationService.addError(translated);
    }
}
